import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import {
  formatCountdownMmSs,
  getMsUntilExpiry,
  isSessionWarningSnoozed,
  snoozeSessionWarning,
} from "../utils/sessionToken";
import { buildLoginUrl, captureCurrentReturnUrl, persistReturnUrl } from "../utils/authRedirect";
import {
  emitAuthExpired,
  isBlockingUiOpen,
  pingSession,
  startSessionKeepAlive,
  stopSessionKeepAlive,
} from "../services/authSession";

const WARNING_LEAD_MS = 5 * 60 * 1000;
const SNOOZE_AFTER_STAY_MS = 30 * 60 * 1000;
const TICK_MS = 1000;

const SessionExpiryWarning = () => {
  const { isAuthenticated, logout } = useUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msRemaining, setMsRemaining] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const keepAliveActiveRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const handleSignOut = useCallback(() => {
    clearTimers();
    if (keepAliveActiveRef.current) {
      stopSessionKeepAlive();
      keepAliveActiveRef.current = false;
    }
    const returnUrl = captureCurrentReturnUrl();
    logout();
    navigate(buildLoginUrl({ timeout: true, returnUrl }));
  }, [clearTimers, logout, navigate]);

  const handleSessionExpired = useCallback(() => {
    clearTimers();
    setOpen(false);
    if (isBlockingUiOpen()) {
      persistReturnUrl(captureCurrentReturnUrl());
      emitAuthExpired();
      return;
    }
    handleSignOut();
  }, [clearTimers, handleSignOut]);

  const openWarning = useCallback((remainingMs: number) => {
    if (isBlockingUiOpen()) return;
    setMsRemaining(Math.min(remainingMs, WARNING_LEAD_MS));
    setRefreshError(null);
    setOpen(true);
  }, []);

  const scheduleNextCheck = useCallback(() => {
    clearTimers();
    setOpen(false);

    if (!isAuthenticated || isSessionWarningSnoozed() || isBlockingUiOpen()) {
      return;
    }

    const token = localStorage.getItem("token");
    const msUntil = getMsUntilExpiry(token);

    if (msUntil == null) {
      return;
    }

    if (msUntil <= 0) {
      handleSessionExpired();
      return;
    }

    if (msUntil <= WARNING_LEAD_MS) {
      openWarning(msUntil);
      tickRef.current = setInterval(() => {
        const left = getMsUntilExpiry(localStorage.getItem("token"));
        if (left == null || left <= 0) {
          handleSessionExpired();
          return;
        }
        setMsRemaining(Math.min(left, WARNING_LEAD_MS));
      }, TICK_MS);
      return;
    }

    timerRef.current = setTimeout(() => {
      if (isBlockingUiOpen()) return;
      const left = getMsUntilExpiry(localStorage.getItem("token"));
      if (left != null && left > 0 && left <= WARNING_LEAD_MS) {
        openWarning(left);
        tickRef.current = setInterval(() => {
          const tickLeft = getMsUntilExpiry(localStorage.getItem("token"));
          if (tickLeft == null || tickLeft <= 0) {
            handleSessionExpired();
            return;
          }
          setMsRemaining(Math.min(tickLeft, WARNING_LEAD_MS));
        }, TICK_MS);
      }
    }, msUntil - WARNING_LEAD_MS);
  }, [clearTimers, handleSessionExpired, isAuthenticated, openWarning]);

  useEffect(() => {
    scheduleNextCheck();
    const recheck = window.setInterval(scheduleNextCheck, 30_000);
    return () => {
      clearTimers();
      window.clearInterval(recheck);
    };
  }, [scheduleNextCheck, clearTimers]);

  const handleStaySignedIn = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      await pingSession();
      snoozeSessionWarning(SNOOZE_AFTER_STAY_MS);
      if (!keepAliveActiveRef.current) {
        startSessionKeepAlive();
        keepAliveActiveRef.current = true;
      }
      clearTimers();
      setOpen(false);
      scheduleNextCheck();
    } catch {
      setRefreshError("Couldn't refresh session. Please sign in again.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    return () => {
      if (keepAliveActiveRef.current) {
        stopSessionKeepAlive();
        keepAliveActiveRef.current = false;
      }
    };
  }, []);

  if (!isAuthenticated) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      data-testid="session-expiry-warning"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Your session is about to expire
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For security, you&apos;ll be signed out in{" "}
          <strong>{formatCountdownMmSs(msRemaining)}</strong>. Stay signed in?
        </Typography>
        {refreshError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {refreshError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", gap: 1 }}>
        {refreshError ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleSignOut}
            sx={{ textTransform: "none" }}
          >
            Go to sign in
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            disabled={refreshing}
            onClick={handleStaySignedIn}
            sx={{ textTransform: "none" }}
            data-testid="session-stay-signed-in"
          >
            {refreshing ? "Checking…" : "Stay signed in"}
          </Button>
        )}
        <Button
          variant="text"
          fullWidth
          onClick={handleSignOut}
          sx={{ textTransform: "none" }}
        >
          Sign out
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiryWarning;
